import { useState } from "react";
import { ChevronDown, Plus, Trash2 } from "lucide-react";
import { money } from "@/studio/ui";
import { sizeStatusOf, num } from "@/studio/rooms";

export const autoLabel = (rooms, type, base) => {
  const n = rooms.filter((r) => r.type === type).length;
  return n === 0 ? base : `${base} ${n + 1}`;
};

export const SIZE_TAG = {
  estimated: ["Est.", "st-tag--est"],
  confirmed: ["Confirmed", "st-tag--ok"],
  unknown: ["Unknown – measure later", "st-tag--unk"],
};

export const SizeTag = ({ status, testId }) => {
  const [txt, cls] = SIZE_TAG[status];
  return <span className={`st-tag ${cls}`} data-testid={testId}>{txt}</span>;
};

export const recFor = (room, serverRooms, previewBudgets) => {
  const srv = serverRooms?.[room.id];
  if (srv && srv.type === room.type && srv.furniture_budget?.rec_min != null) return [srv.furniture_budget.rec_min, srv.furniture_budget.rec_max];
  const p = previewBudgets?.[room.type];
  return p ? [p.min, p.max] : null;
};

const feeText = (room, snap, meta) => {
  const pr = snap?.rooms?.find((r) => r.room_id === room.id);
  const staffType = meta.room_types.find((t) => t.value === room.type)?.staff_fee;
  const max = snap?.max_sizes?.[room.type];
  const oversize = max && num(room.size_sqft) > max;
  if (oversize) return pr && !pr.pending && pr.fee_source === "custom" ? `${money(pr.fee)} · Custom` : "Custom";
  if (staffType) return num(room.staff_fee) != null ? `${money(num(room.staff_fee))} · Staff fee` : "Staff fee";
  return money(snap?.design_fees?.[room.type]?.amount);
};

export const BudgetChoice = ({ room, rec, update, tid }) => (
  <div className="st-field">
    <span className="st-label">Furniture budget</span>
    {rec && (
      <div className="st-seg">
        <button className={room.budget_mode !== "custom" ? "is-on" : ""} onClick={() => update({ budget_mode: "recommended" })} data-testid={`${tid}-budget-recommended`}>Use recommended {money(rec[0])}–{money(rec[1])}</button>
        <button className={room.budget_mode === "custom" ? "is-on" : ""} onClick={() => update({ budget_mode: "custom" })} data-testid={`${tid}-budget-custom`}>Custom</button>
      </div>
    )}
    {(!rec || room.budget_mode === "custom") && (
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <label className="st-money"><span>$</span><input className="st-input" type="number" min="0" style={{ width: 130 }} placeholder={rec ? "Min" : "Amount"} value={room.budget_min} onChange={(e) => update({ budget_mode: "custom", budget_min: e.target.value })} data-testid={`${tid}-budget-min-input`} /></label>
        <span className="st-muted">–</span>
        <label className="st-money"><span>$</span><input className="st-input" type="number" min="0" style={{ width: 130 }} placeholder="Max (optional)" value={room.budget_max} onChange={(e) => update({ budget_mode: "custom", budget_max: e.target.value })} data-testid={`${tid}-budget-max-input`} /></label>
        {!rec && <span className="st-muted st-small">No recommended range for this room type, so enter an amount.</span>}
      </div>
    )}
  </div>
);

const RoomRow = ({ room, index, update, remove, snap, meta, open, toggle, rec }) => {
  const max = snap?.max_sizes?.[room.type];
  const oversize = !!(max && num(room.size_sqft) > max);
  const staffType = meta.room_types.find((t) => t.value === room.type)?.staff_fee;
  const typeLabel = meta.room_types.find((t) => t.value === room.type)?.label;
  const status = sizeStatusOf(room);
  const tid = `room-${index}`;
  return (
    <div className={`st-rrow ${open ? "is-open" : ""}`} data-testid={tid}>
      <button className="st-rrow__head" onClick={toggle} aria-expanded={open} data-testid={`${tid}-toggle`}>
        <span className="st-rrow__name">{room.label || typeLabel}</span>
        <span className="st-rrow__purpose">{room.purpose || <em>No purpose yet</em>}</span>
        <span className="st-rrow__size">{num(room.size_sqft) ? `${num(room.size_sqft)} sq ft` : "—"} <SizeTag status={status} testId={`${tid}-size-tag`} /></span>
        <span className={`st-rrow__fee ${oversize ? "is-warn" : ""}`} data-testid={`${tid}-fee`}>{feeText(room, snap, meta)}</span>
        <ChevronDown size={16} className="st-rrow__chev" />
      </button>
      {open && (
        <div className="st-rrow__body">
          <div className="st-room__fields">
            <label className="st-field"><span className="st-label">Room label · {typeLabel}</span>
              <input className="st-input" value={room.label} onChange={(e) => update({ label: e.target.value })} data-testid={`${tid}-label-input`} /></label>
            <label className="st-field"><span className="st-label">Approx. size (sq ft), if known</span>
              <input className="st-input" type="number" min="1" value={room.size_sqft} onChange={(e) => update({ size_sqft: e.target.value })} placeholder={max ? `Standard up to ${max}` : "Optional"} data-testid={`${tid}-size-input`} /></label>
          </div>
          <label className="st-field"><span className="st-label">What is the purpose of this room?</span>
            <input className="st-input" value={room.purpose} maxLength={300} onChange={(e) => update({ purpose: e.target.value })} placeholder="e.g. Guest room for visiting family" data-testid={`${tid}-purpose-input`} /></label>
          <div className="st-field"><span className="st-label">Measurement</span>
            <div className="st-seg">
              {[["estimated", "Estimated"], ["confirmed", "Confirmed"], ["unknown", "Unknown – measure later"]].map(([v, l]) => (
                <button key={v} className={status === v ? "is-on" : ""} onClick={() => update(v === "unknown" ? { size_status: "unknown", size_sqft: "" } : { size_status: v })}
                  disabled={v !== "unknown" && !num(room.size_sqft)} data-testid={`${tid}-status-${v}`}>{l}</button>
              ))}
            </div>
            {!num(room.size_sqft) && <span className="st-muted st-small">Not required now. Add a size to mark it estimated or confirmed.</span>}
          </div>
          {(staffType || oversize) && (
            <div className="st-room__fee">
              <span>{oversize ? <span className="st-tag st-tag--warn" data-testid={`${tid}-custom-flag`}>Custom pricing required · over {max} sq ft</span> : <span className="st-muted">Staff enters the final design fee</span>}</span>
              <label className="st-money"><span>$</span><input className="st-input" type="number" min="0" value={room.staff_fee} onChange={(e) => update({ staff_fee: e.target.value })} placeholder="Staff fee" data-testid={`${tid}-staff-fee-input`} /></label>
            </div>
          )}
          <BudgetChoice room={room} rec={rec} update={update} tid={tid} />
          <div><button className="st-btn st-btn--quiet st-btn--sm st-btn--danger" onClick={remove} data-testid={`${tid}-remove-button`}><Trash2 size={13} /> Remove room</button></div>
        </div>
      )}
    </div>
  );
};

export const AddRoomBar = ({ meta, onAdd, where }) => (
  <div className={`st-addroom ${where === "top" ? "st-addroom--sticky" : ""}`} data-testid={`room-picker-${where}`}>
    {meta.room_types.map((t) => (
      <button key={t.value} className="st-btn st-btn--ghost st-btn--sm" onClick={() => onAdd(t)} data-testid={`add-room-${t.value}${where === "top" ? "" : "-bottom"}`}><Plus size={13} /> {t.label}</button>
    ))}
  </div>
);

export const newRoom = (rooms, t) => ({ id: crypto.randomUUID(), type: t.value, label: autoLabel(rooms, t.value, t.label), purpose: "", size_sqft: "", staff_fee: "", size_status: "unknown", budget_mode: t.value === "other" ? "custom" : "recommended", budget_min: "", budget_max: "" });

export const RoomsEditor = ({ rooms, change, meta, snap, serverRooms, previewBudgets }) => {
  const [open, setOpen] = useState(null);
  const add = (t) => { const r = newRoom(rooms, t); change((f) => ({ ...f, rooms: [...f.rooms, { ...r, label: autoLabel(f.rooms, t.value, t.label) }] })); setOpen(r.id); };
  const update = (i, patch) => change((f) => ({ ...f, rooms: f.rooms.map((r, j) => (j === i ? { ...r, ...patch } : r)) }));
  const remove = (i) => change((f) => ({ ...f, rooms: f.rooms.filter((_, j) => j !== i) }));

  return (
    <section className="st-panel" data-testid="project-rooms">
      <div className="st-panel__head">
        <h2 className="st-h2">Rooms</h2>
        <span className="st-muted st-small">Number of rooms: <strong data-testid="project-rooms-count">{rooms.length}</strong></span>
      </div>
      <AddRoomBar meta={meta} onAdd={add} where="top" />
      {rooms.length === 0 ? <p className="st-empty">Add the rooms included in this project.</p> : (
        <div className="st-rrows">
          {rooms.map((r, i) => (
            <RoomRow key={r.id} room={r} index={i} snap={snap} meta={meta} open={open === r.id} toggle={() => setOpen(open === r.id ? null : r.id)}
              rec={recFor(r, serverRooms, previewBudgets)} update={(p) => update(i, p)} remove={() => remove(i)} />
          ))}
        </div>
      )}
      {rooms.length > 3 && <AddRoomBar meta={meta} onAdd={add} where="bottom" />}
    </section>
  );
};
